import { getAppVersion } from 'util/appVersion';
import AppSettingsService, { VersionNotes as APIVersionNotes, ReleaseNote as APIReleaseNote } from 'services/appSettingsService';

// Re-export types from the service for backward compatibility
export type ReleaseNote = APIReleaseNote;
export type VersionNotes = APIVersionNotes;

// Fallback release notes data in case API is unavailable
const FALLBACK_RELEASE_NOTES: VersionNotes[] = [
  {
    version: "0.11.0-beta.1",
    date: "2026-04-05",
    notes: [
      {
        type: "enhancement",
        description: "The Community Dashboard now presents strategy, governance, alignment, contributor, and geography signals in a visual enterprise-style briefing.",
      },
      {
        type: "feature",
        description: "Connected accounts now let you link Google to an existing password-based Societea account.",
      },
      {
        type: "enhancement",
        description: "Account settings now show active sessions so you can review and revoke device access.",
      },
      {
        type: "fix",
        description: "Google-linked account deletion and disconnect now revoke the Google connection before the local account link is removed.",
      },
      {
        type: "feature",
        description: "Societea Challenge lets you host a live quiz where players rank issues, choose teabates, guess each other's top priorities, and compare alignment in real time.",
      },
      {
        type: "enhancement",
        description: "Sharing now uses richer /s/ links, lets you send content directly into chat conversations, and opens shared content in an in-chat viewer so you can return to the thread seamlessly.",
      },
    ],
  },
  {
    version: "0.10.2-beta.1",
    date: "2023-06-15",
    notes: [
      {
        type: "enhancement",
        description: "Added beta indicator to clarify expectations",
      },
    ],
  },
  {
    version: "0.10.1",
    date: "2023-06-01",
    notes: [
      {
        type: "feature",
        description: "Added initial version of Teabates for engaging debates around issues",
      },
    ],
  },
];

// Helper functions for release notes
export const getNoteTypeEmoji = (type: ReleaseNote["type"]): string => {
  switch (type) {
    case "feature":
      return "✨"; // Sparkles for new features
    case "enhancement":
      return "🚀"; // Rocket for enhancements
    case "fix":
      return "🐛"; // Bug for fixes
    case "experimental":
      return "🧪"; // Test tube for experimental features
    default:
      return "📝"; // Fallback
  }
};

// Format note type for display
export const formatNoteType = (type: ReleaseNote["type"]): string => {
  switch (type) {
    case "feature":
      return "Features";
    case "enhancement":
      return "Enhancements";
    case "fix":
      return "Fixes";
    case "experimental":
      return "Experimental";
    default:
      return "Changes";
  }
};

// Function to get release notes from API
export const getReleaseNotes = async (): Promise<VersionNotes[]> => {
  try {
    const releaseNotes = await AppSettingsService.getReleaseNotes();
    return releaseNotes.length > 0 ? releaseNotes : FALLBACK_RELEASE_NOTES;
  } catch (error) {
    console.error("Failed to fetch release notes from API, using fallback:", error);
    return FALLBACK_RELEASE_NOTES;
  }
};

// Function to get the current app version from API
export const getCurrentAppVersion = async (): Promise<string> => {
  try {
    const apiVersion = await AppSettingsService.getCurrentVersion();
    return apiVersion;
  } catch (error) {
    console.error("Failed to fetch version from API, using fallback:", error);
    // Fallback to local version
    return getAppVersion();
  }
};

// Synchronous version for backward compatibility
export const getCurrentAppVersionSync = (): string => {
  return getAppVersion();
};

// Export fallback data for backward compatibility
export default FALLBACK_RELEASE_NOTES; 